import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { Button, ConfirmDialog, EmptyState, ErrorState, Field, Input, Money, Notice, PageHeader, Select, SkeletonRows, Surface, Textarea } from "../../../../components/ui";
import CorrectionDetail from "./CorrectionDetail";
import { FINANCE_CORRECTIONS_PATH, HR_CORRECTIONS_PATH, adjustmentsApi, classificationLabel, newRequestKey, toCents } from "./adjustmentView";

// Report 54B (D2): one correction with the actions its state and the viewer allow. `area` is where it was opened from
// ("hr" or "finance") and only decides the way back and which actions are offered first; the server decides whether
// an action may happen, and its refusal is shown in the dialog in its own words.

const REASON_HINT = "Kept in the history of this correction.";

function useField(initial = "") {
  const [value, setValue] = useState(initial);
  return { value, onChange: (event) => setValue(event.target.value), set: setValue };
}

/** A dialog that asks for one written reason. */
function ReasonDialog({ title, description, label, min = 5, confirmLabel, tone, consequence, onSubmit, onClose }) {
  const reason = useField();
  const ok = reason.value.trim().length >= min;
  return (
    <ConfirmDialog title={title} description={description} consequence={consequence} confirmLabel={confirmLabel} tone={tone} confirmDisabled={!ok}
      onConfirm={() => onSubmit(reason.value.trim())} onClose={onClose}>
      <Field label={label} hint={`At least ${min} characters. ${REASON_HINT}`} required>
        <Textarea rows={3} maxLength={1000} value={reason.value} onChange={reason.onChange} />
      </Field>
    </ConfirmDialog>
  );
}

function TaxReviewDialog({ item, onSubmit, onClose }) {
  const amount = useField("0.00");
  const direction = useField("none");
  const basis = useField();
  const cents = toCents(amount.value);
  const signedCents = cents == null ? null : direction.value === "more" ? cents : direction.value === "less" ? -cents : 0;
  const net = signedCents == null ? null : item.grossDifferenceCents - signedCents;
  const ok = signedCents != null && basis.value.trim().length >= 20 && (direction.value === "none" || cents > 0);
  return (
    <ConfirmDialog size="md" title="Record the tax and contribution treatment" confirmLabel="Record treatment" confirmDisabled={!ok}
      description="Say how this correction changes what is withheld from the employee, and what that is based on."
      facts={[
        { label: "Employee", value: item.memberName },
        { label: "Change in gross pay", value: <Money value={item.grossDifferenceCents} /> },
        ...(net != null ? [{ label: item.direction === "PAY_EMPLOYEE" ? "Then to pay the employee" : "Then to recover", value: <Money value={Math.abs(net)} /> }] : []),
      ]}
      consequence="This releases the hold. Finance can then approve or return the correction."
      onConfirm={() => onSubmit({ employee_statutory_difference_cents: signedCents, basis: basis.value.trim() })} onClose={onClose}>
      <div className="space-y-3">
        <Field label="The employee's contributions and tax">
          <Select value={direction.value} onChange={direction.onChange}>
            <option value="none">Do not change</option>
            <option value="more">Go up (more is withheld)</option>
            <option value="less">Go down (less is withheld)</option>
          </Select>
        </Field>
        {direction.value !== "none" ? (
          <Field label="By how much (₱)" error={cents == null ? "Enter an amount in pesos, like 63.63." : undefined} required>
            <Input inputMode="decimal" value={amount.value} onChange={amount.onChange} />
          </Field>
        ) : null}
        <Field label="Basis of this treatment" hint="At least 20 characters: the rule, table or computation you relied on. An unknown amount is never entered as zero." required>
          <Textarea rows={3} maxLength={1000} value={basis.value} onChange={basis.onChange} />
        </Field>
      </div>
    </ConfirmDialog>
  );
}

function CashReturnDialog({ item, onSubmit, onClose }) {
  const amount = useField((item.outstandingCents / 100).toFixed(2));
  const reference = useField();
  const [key] = useState(newRequestKey);
  const cents = toCents(amount.value);
  const tooMuch = cents != null && cents > item.outstandingCents;
  const ok = cents > 0 && !tooMuch && reference.value.trim().length >= 3;
  return (
    <ConfirmDialog size="md" title={`Record cash received from ${item.memberName}?`} confirmLabel="Record cash received" confirmDisabled={!ok}
      facts={[{ label: "Still outstanding", value: <Money value={item.outstandingCents} /> }]}
      consequence="This lowers what the employee still owes by the amount received. It cannot be undone; a mistake is corrected with a reversal."
      onConfirm={() => onSubmit({ amount_cents: cents, reference: reference.value.trim() }, key)} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Cash received now (₱)" error={cents == null ? "Enter an amount in pesos." : tooMuch ? "That is more than what is outstanding." : undefined} required>
          <Input inputMode="decimal" value={amount.value} onChange={amount.onChange} />
        </Field>
        <Field label="Receipt reference" hint="The receipt or count-sheet number that shows the cash was received." required>
          <Input maxLength={200} value={reference.value} onChange={reference.onChange} />
        </Field>
      </div>
    </ConfirmDialog>
  );
}

function DisputeAnswerDialog({ item, onSubmit, onClose }) {
  const outcome = useField("uphold");
  const resolution = useField();
  return (
    <ConfirmDialog size="md" title="Answer the employee's dispute" confirmLabel={outcome.value === "uphold" ? "Keep the claim" : "Withdraw what is outstanding"}
      tone={outcome.value === "withdraw" ? "danger" : "default"} confirmDisabled={resolution.value.trim().length < 20}
      facts={[{ label: "The employee says", value: item.disputeReason }, { label: "Recovered so far", value: <Money value={item.settledCents} /> }, { label: "Still outstanding", value: <Money value={item.outstandingCents} /> }]}
      consequence={outcome.value === "uphold" ? "The claim stands and recovery resumes. The employee sees your answer." : "What is still outstanding is dropped. Anything already recovered stays recorded; returning it needs a reversal."}
      onConfirm={() => onSubmit({ outcome: outcome.value, resolution: resolution.value.trim() })} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Decision">
          <Select value={outcome.value} onChange={outcome.onChange}>
            <option value="uphold">The claim stands</option>
            <option value="withdraw">Withdraw what is still outstanding</option>
          </Select>
        </Field>
        <Field label="Your answer to the employee" hint="At least 20 characters." required>
          <Textarea rows={3} maxLength={1000} value={resolution.value} onChange={resolution.onChange} />
        </Field>
      </div>
    </ConfirmDialog>
  );
}

function HandoverDialog({ item, onSubmit, onClose }) {
  const reference = useField();
  const [key] = useState(newRequestKey);
  return (
    <ConfirmDialog title={`Record handing ${item.memberName} this cash?`} confirmLabel="Record cash handover"
      facts={[{ label: "Amount", value: <Money value={item.amountCents} /> }, { label: "For", value: `${classificationLabel(item.classification)}, ${item.periodName}` }]}
      consequence="This is not yet a payment: it is paid once the employee confirms receiving it."
      onConfirm={() => onSubmit({ reference: reference.value.trim() || undefined }, key)} onClose={onClose}>
      <Field label="Reference" hint="Optional: a voucher or count-sheet number.">
        <Input maxLength={200} value={reference.value} onChange={reference.onChange} />
      </Field>
    </ConfirmDialog>
  );
}

export default function CorrectionPage({ area = "hr" }) {
  const { adjustmentId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", item: null });
  const [dialog, setDialog] = useState(null);
  const [notice, setNotice] = useState(null);
  const listPath = area === "finance" ? FINANCE_CORRECTIONS_PATH : HR_CORRECTIONS_PATH;

  const load = useCallback(async () => {
    try {
      const item = await adjustmentsApi.get(adjustmentId);
      setState(item?.id ? { status: "ready", item } : { status: "missing", item: null });
    } catch (error) {
      const code = error?.response?.status ?? error?.status;
      setState({ status: code === 404 || code === 403 ? "missing" : "error", item: null });
    }
  }, [adjustmentId]);
  useEffect(() => { setState({ status: "loading", item: null }); load(); }, [load]);

  const back = <Button as={Link} to={listPath} variant="quiet" size="sm" icon={ArrowLeft} className="-ml-3 mb-2">All corrections</Button>;
  if (state.status !== "ready") {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <div>{back}<PageHeader title="Correction" /></div>
        <Surface>
          {state.status === "loading" ? <SkeletonRows rows={6} columns={2} label="Loading the correction" />
            : state.status === "missing" ? <EmptyState title="This correction isn't available" description="It may belong to another business, or the link is wrong." />
              : <ErrorState title="The correction couldn't be loaded" description="Check your connection and try again. Nothing has changed." onRetry={load} />}
        </Surface>
      </div>
    );
  }

  const { item } = state;
  const viewer = item.viewer || {};
  // What the server did is said once; the page then shows the state the server returns.
  const run = (work, done) => async (...args) => {
    await work(...args);
    setDialog(null);
    setNotice(done);
    await load();
  };
  const submitted = item.status === "SUBMITTED";
  const approved = item.status === "APPROVED";
  const pay = item.direction === "PAY_EMPLOYEE";
  const actions = [];
  if (viewer.canManage && ["DRAFT", "RETURNED"].includes(item.status)) actions.push(<Button key="submit" variant="primary" onClick={() => setDialog("submit")}>Send to Finance</Button>);
  if (viewer.canDecide && submitted && item.statutoryState !== "NOT_AFFECTED") actions.push(<Button key="tax" variant={item.statutoryState === "REVIEW_REQUIRED" ? "primary" : "secondary"} onClick={() => setDialog("tax")}>{item.statutoryState === "REVIEW_REQUIRED" ? "Record tax treatment" : "Change tax treatment"}</Button>);
  if (viewer.canDecide && submitted && item.statutoryState !== "REVIEW_REQUIRED") actions.push(<Button key="approve" variant="primary" onClick={() => setDialog("approve")}>Approve</Button>);
  if (viewer.canDecide && submitted) actions.push(<Button key="return" onClick={() => setDialog("return")}>Return to HR</Button>, <Button key="reject" onClick={() => setDialog("reject")}>Reject</Button>);
  if (viewer.canRecordMoney && approved && pay && item.settlementStatus === "OPEN") actions.push(<Button key="handover" variant="primary" onClick={() => setDialog("handover")}>Record cash handover</Button>);
  if (viewer.canRecordMoney && approved && !pay && item.recoveryMethod === "CASH_RETURN" && ["OPEN", "PARTIAL"].includes(item.settlementStatus) && item.employeeResponse !== "DISPUTED") actions.push(<Button key="return-cash" variant="primary" onClick={() => setDialog("cash-return")}>Record cash received</Button>);
  if (viewer.canDecide && approved && item.employeeResponse === "DISPUTED") actions.push(<Button key="dispute" variant="primary" onClick={() => setDialog("dispute")}>Answer the dispute</Button>);
  if (viewer.isOwner && approved && item.settlementStatus === "HANDED_OVER") actions.push(<Button key="behalf" onClick={() => setDialog("behalf")}>Confirm for the employee</Button>);
  if (viewer.canManage && approved && item.settledCents > 0 && !item.reversesAdjustmentId) actions.push(<Button key="reverse" onClick={() => setDialog("reverse")}>Raise a reversal</Button>);
  const cancellable = (viewer.canManage && ["DRAFT", "RETURNED", "SUBMITTED"].includes(item.status)) || (viewer.canDecide && approved && item.settlementStatus === "OPEN" && item.settledCents === 0);
  if (cancellable) actions.push(<Button key="cancel" variant="quiet" onClick={() => setDialog("cancel")}>Cancel correction</Button>);

  const close = () => setDialog(null);
  const who = item.memberName;
  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <div>
        {back}
        <PageHeader
          title={`${classificationLabel(item.classification)} — ${who}`}
          description={`${item.number} · ${item.periodName}${item.reversesAdjustmentId ? " · reversal of an earlier correction" : ""}`}
          actions={actions.length ? <div className="flex flex-wrap gap-2 max-sm:flex-col">{actions}</div> : null}
        />
      </div>
      {notice ? <Notice tone="success" role="status">{notice}</Notice> : null}
      {submitted && viewer.isRaiser ? <Notice>You raised this correction, so someone else in Finance reviews and approves it.</Notice> : null}

      <CorrectionDetail item={item} payslipTo={`/business/hr/payroll/payslips/${item.payslipId}`} />

      {dialog === "submit" ? (
        <ConfirmDialog title={`Send this correction for ${who} to Finance?`} confirmLabel="Send to Finance"
          consequence="Finance reviews the tax treatment where needed, then approves, returns or rejects it. Nothing is paid or recovered until then."
          onConfirm={run(() => adjustmentsApi.submit(item.id), "Sent to Finance.")} onClose={close} />
      ) : null}
      {dialog === "tax" ? <TaxReviewDialog item={item} onClose={close} onSubmit={run((data) => adjustmentsApi.reviewStatutory(item.id, data), "Tax treatment recorded. The correction can now be approved.")} /> : null}
      {dialog === "approve" ? (
        <ReasonDialog title={pay ? `Approve paying ${who}?` : `Approve recovering from ${who}?`} label="What you checked" confirmLabel={pay ? "Approve payment" : "Approve recovery"}
          description={<>Amount: <Money value={item.amountCents} className="font-medium text-foreground" /></>}
          consequence={pay ? "The amount becomes owed to the employee. It is paid when the cash is handed over and the employee confirms it." : item.recoveryMethod === "FUTURE_PAY" ? "An installment is taken from each coming salary. The employee is told and can dispute it." : "The employee is told and can acknowledge or dispute it. The claim falls only by cash Finance records receiving."}
          onClose={close} onSubmit={run((remarks) => adjustmentsApi.approve(item.id, remarks), "Approved.")} />
      ) : null}
      {dialog === "return" ? <ReasonDialog title="Return this correction to HR?" label="What HR should fix or add" confirmLabel="Return to HR" onClose={close} onSubmit={run((remarks) => adjustmentsApi.returnToHr(item.id, remarks), "Returned to HR.")} /> : null}
      {dialog === "reject" ? <ReasonDialog title="Reject this correction?" label="Why it is rejected" confirmLabel="Reject correction" tone="danger" consequence="Nothing is paid or recovered. HR can raise a new correction with better evidence." onClose={close} onSubmit={run((remarks) => adjustmentsApi.reject(item.id, remarks), "Rejected.")} /> : null}
      {dialog === "cancel" ? <ReasonDialog title="Cancel this correction?" label="Why it is cancelled" confirmLabel="Cancel correction" tone="danger" consequence="Nothing is paid or recovered. The correction stays in the history as cancelled." onClose={close} onSubmit={run((reason) => adjustmentsApi.cancel(item.id, reason), "Cancelled.")} /> : null}
      {dialog === "handover" ? <HandoverDialog item={item} onClose={close} onSubmit={run((data, key) => adjustmentsApi.cashHandover(item.id, data, key), "Cash handover recorded. It is paid once the employee confirms it.")} /> : null}
      {dialog === "cash-return" ? <CashReturnDialog item={item} onClose={close} onSubmit={run((data, key) => adjustmentsApi.cashReturn(item.id, data, key), "Cash received was recorded.")} /> : null}
      {dialog === "dispute" ? <DisputeAnswerDialog item={item} onClose={close} onSubmit={run((data) => adjustmentsApi.resolveDispute(item.id, data), "Your answer was recorded and the employee was told.")} /> : null}
      {dialog === "behalf" ? (
        <ReasonDialog title={`Confirm for ${who} that this cash was received?`} label="Why the employee cannot confirm it" min={10} confirmLabel="Confirm for the employee"
          description={<>Amount: <Money value={item.amountCents} className="font-medium text-foreground" /></>}
          consequence="Use this only when the employee has left or cannot be reached. It is recorded as confirmed by the owner, with your reason."
          onClose={close} onSubmit={run((reason) => adjustmentsApi.confirmOnBehalf(item.id, reason), "Recorded as confirmed by the owner for the employee.")} />
      ) : null}
      {dialog === "reverse" ? (
        <ReasonDialog title="Raise a reversal of this correction?" label="Why it must be reversed" min={10} confirmLabel="Raise reversal"
          description={<>Money that moved: <Money value={item.settledCents} className="font-medium text-foreground" /></>}
          consequence="A new correction for the opposite direction is drafted and goes through Finance again. This one stays in the history unchanged."
          onClose={close}
          onSubmit={async (reason) => {
            const created = await adjustmentsApi.reverse(item.id, { reason }, newRequestKey());
            setDialog(null);
            if (created?.id) navigate(`${listPath}/${created.id}`);
          }} />
      ) : null}
    </div>
  );
}
